// =============================================================================
// DeliveryNotesPage — list
// =============================================================================
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, getApiError, type PaginatedResult } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { PageScaffold } from '../../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../../components/DataTable';
import { Button } from '../../../components/Button';
import { StatusPill } from '../../../components/StatusPill';
import { EmptyState } from '../../../components/EmptyState';
import { cn } from '../../../lib/cn';
import { DocumentExportButtons } from '../../../components/DocumentExportButtons';
import { Pagination } from '../../../components/Pagination';

const PAGE_SIZE = 20;

type DnStatus = 'DRAFT' | 'IN_TRANSIT' | 'DELIVERED' | 'REJECTED' | 'RETURNED' | 'FAILED';

interface DnRow {
  id: string;
  number: string;
  status: DnStatus;
  scheduledDate: string | null;
  dispatchedAt: string | null;
  deliveredAt: string | null;
  order: {
    id: string;
    number: string;
    customer: { name: string; companyName: string | null };
    deliveryAddress: string | null;
    deliveryAddressPrecise: string | null;
  };
  driver: { id: string; name: string } | null;
}

interface DeliveryNotesResult extends PaginatedResult<DnRow> {
  counts: Record<DnStatus, number> & { all: number };
}

type Tab = 'all' | 'draft' | 'in_transit' | 'delivered' | 'failed' | 'rejected';
const TABS: { key: Tab; label: string; statuses: DnStatus[] }[] = [
  { key: 'all', label: 'All', statuses: [] },
  { key: 'draft', label: 'Draft', statuses: ['DRAFT'] },
  { key: 'in_transit', label: 'In transit', statuses: ['IN_TRANSIT'] },
  { key: 'delivered', label: 'Delivered', statuses: ['DELIVERED'] },
  { key: 'failed', label: 'Failed / reschedule', statuses: ['FAILED'] },
  { key: 'rejected', label: 'Rejected', statuses: ['REJECTED', 'RETURNED'] },
];

function tone(s: DnStatus): 'neutral' | 'warning' | 'positive' | 'negative' {
  if (s === 'DELIVERED') return 'positive';
  if (s === 'IN_TRANSIT' || s === 'FAILED') return 'warning';
  if (s === 'REJECTED' || s === 'RETURNED') return 'negative';
  return 'neutral';
}

export function DeliveryNotesPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('all');
  const [page, setPage] = useState(1);
  const status = TABS.find((t) => t.key === tab)!.statuses.join(',');
  const list = useQuery({
    queryKey: ['delivery-notes', { status, page }],
    queryFn: () =>
      apiGet<DeliveryNotesResult>('/delivery-notes', {
        page,
        pageSize: PAGE_SIZE,
        ...(status ? { status } : {}),
      }),
    refetchInterval: 60_000,
  });

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const counts = list.data?.counts;
  const safePage = list.data?.page ?? page;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PageScaffold
      scroll={false}
      title="Delivery Notes"
      actions={<DocumentExportButtons document="delivery-notes" />}
      subtitle="Drivers carry these. Customer signs on delivery; a manager approves to issue the tax invoice. Goods left stock at order confirm."
    >
      <Card className="shrink-0" bodyClassName="p-4">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs uppercase tracking-wide transition border',
                tab === t.key
                  ? 'bg-accent text-white border-accent'
                  : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
              )}
            >
              <span>{t.label}</span>
              <span
                className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded',
                  tab === t.key ? 'bg-white/20 text-white' : 'bg-surface text-ink-3',
                )}
              >
                {t.key === 'all'
                  ? (counts?.all ?? 0)
                  : t.statuses.reduce((sum, key) => sum + (counts?.[key] ?? 0), 0)}
              </span>
            </button>
          ))}
        </div>
      </Card>

      <DataTableCard
        footer={
          total > 0 ? (
            <Pagination
              page={safePage}
              pageCount={pageCount}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              unit="delivery note"
            />
          ) : undefined
        }
      >
        {list.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : list.isError ? (
          <EmptyState
            title="Could not load delivery notes"
            description={getApiError(list.error).message}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No delivery notes here"
            description="Delivery notes are created from a confirmed delivery order."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">DN</th>
                <th className="px-6 py-2 font-medium">Order</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium">Address</th>
                <th className="px-6 py-2 font-medium">Driver</th>
                <th className="px-6 py-2 font-medium">When</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const display = d.order.customer.companyName ?? d.order.customer.name;
                const whenLabel = d.deliveredAt
                  ? `Delivered ${new Date(d.deliveredAt).toLocaleDateString('en-MU', { day: 'numeric', month: 'short' })}`
                  : d.dispatchedAt
                    ? `Out ${new Date(d.dispatchedAt).toLocaleDateString('en-MU', { day: 'numeric', month: 'short' })}`
                    : d.scheduledDate
                      ? `Sched ${new Date(d.scheduledDate).toLocaleDateString('en-MU', { day: 'numeric', month: 'short' })}`
                      : 'No date';
                return (
                  <tr key={d.id} className="border-b border-rule-2 hover:bg-paper-2">
                    <td className="px-6 py-3 align-top">
                      <Link
                        to={`/delivery-notes/${d.id}`}
                        className="kc-mono text-xs text-ink hover:text-accent"
                      >
                        {d.number}
                      </Link>
                    </td>
                    <td className="px-6 py-3 align-top">
                      <Link
                        to={`/orders/${d.order.id}`}
                        className="kc-mono text-xs text-ink-2 hover:text-accent"
                      >
                        {d.order.number}
                      </Link>
                    </td>
                    <td className="px-6 py-3 align-top text-ink">{display}</td>
                    <td className="px-6 py-3 align-top text-xs text-ink-3 max-w-xs truncate">
                      {d.order.deliveryAddressPrecise || d.order.deliveryAddress || '—'}
                    </td>
                    <td className="px-6 py-3 align-top text-xs text-ink-2">
                      {d.driver?.name ?? <span className="italic text-ink-3">unassigned</span>}
                    </td>
                    <td className="px-6 py-3 align-top text-xs text-ink-3">{whenLabel}</td>
                    <td className="px-6 py-3 align-top">
                      <StatusPill tone={tone(d.status)}>
                        {d.status.toLowerCase().replace('_', ' ')}
                      </StatusPill>
                    </td>
                    <td className="px-6 py-3 align-top text-right">
                      <Link to={`/delivery-notes/${d.id}`}>
                        <Button size="sm" variant="ghost">
                          Open
                        </Button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}
